import {
  type ColorPalette,
  defineRecipe,
  type RecipeDefinition,
  type SystemStyleObject,
} from '@chakra-ui/react'

import { FontWeight } from '../textStyles/defaultTextStyles'
import { keyboardFocusRing } from './focusRing'

/**
 * web-app `MangoButtonVariant` values that this recipe covers.
 * Unmapped variants (`Brand`, `Login`, and the other `_100` / `_200`s) stay
 * on `MangoButton` until a later ticket.
 */
export const mangoButtonVariantToRecipe = {
  default_300: 'primary',
  default_200: 'secondary',
  default_100: 'tertiary',
  destructive_300: 'destructive',
  success_300: 'success',
  neutral_300: 'neutral',
  brand_text: 'link',
} as const

export type MappedMangoButtonVariant = keyof typeof mangoButtonVariantToRecipe

export type SemanticButtonVariant =
  (typeof mangoButtonVariantToRecipe)[MappedMangoButtonVariant]

export type BuiltInButtonVariant =
  'solid' | 'subtle' | 'surface' | 'outline' | 'ghost' | 'plain'

export type ButtonRecipeVariant = SemanticButtonVariant | BuiltInButtonVariant

/**
 * Chakra's generated `Button` types only list the built-in variants.
 * Cast a recipe variant so `<Button variant={asButtonRecipeVariant('primary')}>`
 * type-checks until Chakra typegen includes the semantic names.
 */
export const asButtonRecipeVariant = (
  variant: ButtonRecipeVariant,
): BuiltInButtonVariant => variant as BuiltInButtonVariant

// Chakra's generated ColorPalette union only lists the built-in names.
const themeBrandPalette: string = 'brand'
const brandPalette = themeBrandPalette as ColorPalette

/**
 * Disabled fills for the semantic variants. Built-ins keep the base fade, so
 * a disabled button with its own `bg` or `color` props still looks disabled.
 */
const disabledFill: SystemStyleObject = {
  opacity: '1',
  boxShadow: 'none',
  _hover: { boxShadow: 'none' },
  _active: { boxShadow: 'none' },
}

const disabledMutedFill: SystemStyleObject = {
  bg: 'bg.muted',
  ...disabledFill,
  _hover: { bg: 'bg.muted', boxShadow: 'none' },
  _active: { bg: 'bg.muted', boxShadow: 'none' },
}

const disabledEmphasizedFill: SystemStyleObject = {
  bg: 'bg.emphasized',
  ...disabledFill,
  _hover: { bg: 'bg.emphasized', boxShadow: 'none' },
  _active: { bg: 'bg.emphasized', boxShadow: 'none' },
}

const disabledSubtleFill: SystemStyleObject = {
  bg: 'bg.subtle',
  color: 'fg.muted',
  ...disabledFill,
  _hover: { bg: 'bg.subtle', boxShadow: 'none' },
  _active: { bg: 'bg.subtle', boxShadow: 'none' },
}

const disabledTransparent: SystemStyleObject = {
  bg: 'transparent',
  color: 'fg.muted',
  ...disabledFill,
  _hover: { bg: 'transparent', boxShadow: 'none' },
  _active: { bg: 'transparent', boxShadow: 'none' },
}

/**
 * Open menu and popover triggers. Chakra's `_expanded` also matches
 * `[data-expanded]`, which an open tooltip sets on its trigger, so a tooltip
 * trigger would take this fill on hover and keyboard focus.
 */
export const popupTriggerOpen = '&[aria-expanded=true]'

/**
 * Chakra's built-in variants fill `_expanded`, and that recipe merges under
 * this one. Pin it to the rest fill so an open tooltip changes nothing;
 * `popupTriggerOpen` comes later in the cascade and still wins.
 */
const restFillWhenTooltipOpen = (bg: string): SystemStyleObject => ({
  _expanded: { bg },
})

/**
 * Open fills for solids. A menu stays open for as long as the user needs, so
 * the fill must not be lighter than the rest fill under the contrast text;
 * `emphasized` is the light 300 step in light mode.
 * White text in both modes: the 700 step in both modes.
 */
const whiteTextOpenFill: SystemStyleObject = {
  bg: { base: 'colorPalette.fg', _dark: 'colorPalette.emphasized' },
}

/** Orange: white text in light mode, black in dark (700, then 300). */
const mixedTextOpenFill: SystemStyleObject = { bg: 'colorPalette.fg' }

/** Yellow: black text in both modes, so the fill lightens (300 in both). */
const blackTextOpenFill: SystemStyleObject = {
  bg: { base: 'colorPalette.emphasized', _dark: 'colorPalette.fg' },
}

/** `MangoButtonVariant.Default_300` */
const primaryStyle: SystemStyleObject = {
  bg: 'brand.solid',
  color: 'brand.contrast',
  textStyle: 'Body2_Semibold',
  fontWeight: FontWeight.SemiBold,
  _hover: {
    bg: 'brand.emphasized',
    boxShadow: 'focus',
  },
  _active: {
    bg: 'brand.emphasized',
    boxShadow: 'focus',
  },
  [popupTriggerOpen]: {
    bg: { base: 'brand.fg', _dark: 'brand.emphasized' },
    boxShadow: 'focus',
  },
  _disabled: {
    color: 'brand.contrast',
    ...disabledEmphasizedFill,
    _hover: { bg: 'bg.emphasized', boxShadow: 'none', color: 'brand.contrast' },
    _active: {
      bg: 'bg.emphasized',
      boxShadow: 'none',
      color: 'brand.contrast',
    },
  },
}

/** `MangoButtonVariant.Default_200` */
const secondaryStyle: SystemStyleObject = {
  bg: 'bg.muted',
  color: 'fg.default',
  fontWeight: FontWeight.Normal,
  _hover: { bg: 'bg.emphasized' },
  _active: { bg: 'bg.emphasized' },
  [popupTriggerOpen]: { bg: 'bg.emphasized' },
  _disabled: disabledSubtleFill,
}

/** `MangoButtonVariant.Default_100` */
const tertiaryStyle: SystemStyleObject = {
  bg: 'transparent',
  color: 'fg.default',
  fontWeight: FontWeight.Normal,
  _hover: { bg: 'bg.muted' },
  _active: { bg: 'bg.muted' },
  [popupTriggerOpen]: { bg: 'bg.muted' },
  _disabled: disabledTransparent,
}

/** `MangoButtonVariant.Destructive_300` */
const destructiveStyle: SystemStyleObject = {
  bg: 'red.solid',
  color: 'red.contrast',
  textStyle: 'Body2_Semibold',
  fontWeight: FontWeight.SemiBold,
  _hover: {
    bg: 'red.emphasized',
    boxShadow: 'error',
  },
  _active: {
    bg: 'red.solid',
    boxShadow: 'error',
  },
  [popupTriggerOpen]: {
    bg: 'red.solid',
    boxShadow: 'error',
  },
  _disabled: {
    color: 'red.contrast',
    ...disabledEmphasizedFill,
    _hover: { bg: 'bg.emphasized', boxShadow: 'none', color: 'red.contrast' },
    _active: { bg: 'bg.emphasized', boxShadow: 'none', color: 'red.contrast' },
  },
}

/** `MangoButtonVariant.Success_300` */
const successStyle: SystemStyleObject = {
  bg: 'green.solid',
  color: 'green.contrast',
  textStyle: 'Body2_Semibold',
  fontWeight: FontWeight.SemiBold,
  _hover: {
    bg: 'green.emphasized',
    boxShadow: 'success',
  },
  _active: {
    bg: 'green.solid',
    boxShadow: 'success',
  },
  [popupTriggerOpen]: {
    bg: 'green.solid',
    boxShadow: 'success',
  },
  _disabled: {
    color: 'green.contrast',
    ...disabledMutedFill,
    _hover: { bg: 'bg.muted', boxShadow: 'none', color: 'green.contrast' },
    _active: { bg: 'bg.muted', boxShadow: 'none', color: 'green.contrast' },
  },
}

/** `MangoButtonVariant.Neutral_300` */
const neutralStyle: SystemStyleObject = {
  bg: 'bg.default',
  color: 'fg.muted',
  textStyle: 'Body2_Semibold',
  fontWeight: FontWeight.SemiBold,
  _hover: {
    bg: 'bg.emphasized',
    boxShadow: 'default',
  },
  _active: {
    bg: 'bg.emphasized',
    boxShadow: '0 0.25rem 0.75rem 0 {colors.bg.muted}',
  },
  [popupTriggerOpen]: {
    bg: 'bg.emphasized',
    boxShadow: 'default',
  },
  _disabled: {
    color: 'fg.muted',
    ...disabledMutedFill,
    _hover: { bg: 'bg.muted', boxShadow: 'none', color: 'fg.muted' },
    _active: { bg: 'bg.muted', boxShadow: 'none', color: 'fg.muted' },
  },
}

/** `MangoButtonVariant.Brand_Text` */
const linkStyle: SystemStyleObject = {
  bg: 'transparent',
  color: 'brand.fg',
  fontWeight: FontWeight.Normal,
  _hover: { bg: 'transparent', color: 'brand.fg' },
  _active: { bg: 'transparent', color: 'brand.fg' },
  [popupTriggerOpen]: { bg: 'transparent', color: 'brand.fg' },
  _disabled: disabledTransparent,
}

/**
 * Hover for gray and neutral solids. `emphasized` on those ramps is a light
 * step under light text, so the fill moves to `fg`.
 *
 * `solid` states change the fill only, like Chakra's `solid`. Many web-app
 * buttons have no `variant` and set their own `css`, so a state shadow or
 * color here would leak into them.
 */
const neutralSolidHover: SystemStyleObject = { bg: 'colorPalette.fg' }

const emphasizedSolidHover: SystemStyleObject = {
  bg: 'colorPalette.emphasized',
}

/**
 * Built-in `solid` — `primary` when no `colorPalette` is passed, and follows
 * an explicit `colorPalette` (`colorPalette="error"` matches `destructive`).
 * The `brand` palette here sets the color variables; the `defaultVariants`
 * palette only selects the brand compound hover. Both replace a palette
 * inherited from a parent element.
 */
const solidStyle: SystemStyleObject = {
  colorPalette: 'brand',
  bg: 'colorPalette.solid',
  color: 'colorPalette.contrast',
  borderColor: 'transparent',
  textStyle: 'Body2_Semibold',
  fontWeight: FontWeight.SemiBold,
  _hover: neutralSolidHover,
  _active: neutralSolidHover,
  ...restFillWhenTooltipOpen('colorPalette.solid'),
  [popupTriggerOpen]: neutralSolidHover,
}

/** Built-in `outline` — Chakra's palette border, with the Mango hover. */
const outlineStyle: SystemStyleObject = {
  bg: 'transparent',
  color: 'colorPalette.fg',
  borderWidth: '1px',
  '--outline-color-legacy': 'colors.colorPalette.muted',
  '--outline-color': 'colors.colorPalette.border',
  borderColor: 'var(--outline-color, var(--outline-color-legacy))',
  fontWeight: FontWeight.Normal,
  _hover: { bg: 'colorPalette.subtle' },
  _active: { bg: 'colorPalette.subtle' },
  ...restFillWhenTooltipOpen('transparent'),
  [popupTriggerOpen]: { bg: 'colorPalette.subtle' },
}

/** Built-in `ghost` — nearest to `tertiary`, follows `colorPalette`. */
const ghostStyle: SystemStyleObject = {
  bg: 'transparent',
  color: 'colorPalette.fg',
  fontWeight: FontWeight.Normal,
  _hover: { bg: 'colorPalette.subtle' },
  _active: { bg: 'colorPalette.subtle' },
  ...restFillWhenTooltipOpen('transparent'),
  [popupTriggerOpen]: { bg: 'colorPalette.subtle' },
}

const subtleStyle: SystemStyleObject = {
  bg: 'colorPalette.subtle',
  color: 'colorPalette.fg',
  borderColor: 'transparent',
  fontWeight: FontWeight.Normal,
  _hover: { bg: 'colorPalette.muted' },
  ...restFillWhenTooltipOpen('colorPalette.subtle'),
  [popupTriggerOpen]: { bg: 'colorPalette.muted' },
}

const surfaceStyle: SystemStyleObject = {
  bg: 'colorPalette.subtle',
  color: 'colorPalette.fg',
  fontWeight: FontWeight.Normal,
  shadow: '0 0 0px 1px var(--shadow-color)',
  shadowColor: 'colorPalette.muted',
  _hover: { bg: 'colorPalette.muted' },
  ...restFillWhenTooltipOpen('colorPalette.subtle'),
  [popupTriggerOpen]: { bg: 'colorPalette.muted' },
}

const plainStyle: SystemStyleObject = {
  color: 'colorPalette.fg',
  fontWeight: FontWeight.Normal,
}

const bodyText: SystemStyleObject = {
  textStyle: 'Body2',
  fontSize: '14px',
  fontWeight: FontWeight.Normal,
  lineHeight: '20px',
}

const whiteTextSolidPalettes = [
  'brand',
  'red',
  'error',
  'green',
  'success',
  'blue',
  'info',
  'cyan',
  'teal',
  'purple',
  'accent',
  'pink',
] as const

const mixedTextSolidPalettes = ['orange', 'warning'] as const

const blackTextSolidPalettes = ['yellow'] as const

export const buttonRecipe = defineRecipe({
  className: 'chakra-button',
  base: {
    display: 'inline-flex',
    appearance: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    position: 'relative',
    borderRadius: 'sm',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    borderWidth: '0',
    borderColor: 'transparent',
    cursor: 'button',
    flexShrink: '0',
    outline: '0',
    isolation: 'isolate',
    gap: '0.5rem',
    ...bodyText,
    transitionProperty: 'common',
    transitionDuration: 'moderate',
    _disabled: {
      cursor: 'not-allowed',
      opacity: '0.5',
      layerStyle: 'none',
      userSelect: 'none',
    },
    ...keyboardFocusRing,
    _icon: {
      flexShrink: '0',
    },
  },
  variants: {
    variant: {
      primary: primaryStyle,
      secondary: secondaryStyle,
      tertiary: tertiaryStyle,
      destructive: destructiveStyle,
      success: successStyle,
      neutral: neutralStyle,
      link: linkStyle,
      solid: solidStyle,
      outline: outlineStyle,
      ghost: ghostStyle,
      subtle: subtleStyle,
      surface: surfaceStyle,
      plain: plainStyle,
    },
  },
  defaultVariants: {
    variant: 'solid',
    colorPalette: brandPalette,
  },
  // `neutral`, `error`, and `success` are theme palettes. Chakra's generated
  // ColorPalette union only lists the built-in names.
  compoundVariants: [
    {
      variant: 'solid',
      colorPalette: ['gray', 'neutral'],
      css: {
        _hover: neutralSolidHover,
        _active: neutralSolidHover,
        [popupTriggerOpen]: neutralSolidHover,
      },
    },
    {
      variant: 'solid',
      colorPalette: [...whiteTextSolidPalettes],
      css: {
        _hover: emphasizedSolidHover,
        _active: emphasizedSolidHover,
        [popupTriggerOpen]: whiteTextOpenFill,
      },
    },
    {
      variant: 'solid',
      colorPalette: [...mixedTextSolidPalettes],
      css: {
        _hover: emphasizedSolidHover,
        _active: emphasizedSolidHover,
        [popupTriggerOpen]: mixedTextOpenFill,
      },
    },
    {
      variant: 'solid',
      colorPalette: [...blackTextSolidPalettes],
      css: {
        _hover: emphasizedSolidHover,
        _active: emphasizedSolidHover,
        [popupTriggerOpen]: blackTextOpenFill,
      },
    },
  ] as RecipeDefinition['compoundVariants'],
})
